/**
 * Beeldbellen: begeleide opzet en statusmeldingen voor de behandelaar.
 * De cliënt opent een link en ziet de bal op het eigen scherm; hier staat wat
 * de behandelaar daarvoor moet doen en wat de cliënt op dit moment ziet.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import QRCode from "qrcode";
import {
  BEELDBELLEN_EVENT,
  ensureBeeldbellenSession,
  getBeeldbellenState,
  stopBeeldbellenSession,
  type BeeldbellenState,
} from "../engine/ws-remote-host";
import { SMOOTH_FPS_MIN } from "../lib/beeldbellen-status";
import { ClientStatusBanner, CopyInvitationButton } from "./ClientStatusBanner";
import "../beeldbellen.css";

const ARRANGEMENT_KEY = "emdr-beeldbellen-opstelling";
const TEST_ROUND_MS = 10_000;

type Arrangement = "naast-elkaar" | "telefoon";

export function useBeeldbellen(): BeeldbellenState {
  const [state, setState] = useState<BeeldbellenState>(getBeeldbellenState);
  useEffect(() => {
    const sync = () => setState(getBeeldbellenState());
    window.addEventListener(BEELDBELLEN_EVENT, sync);
    sync();
    return () => window.removeEventListener(BEELDBELLEN_EVENT, sync);
  }, []);
  return state;
}

function Check({ done, children }: { done: boolean; children: ReactNode }) {
  return (
    <li className={`bb-check${done ? " is-done" : ""}`}>
      <span className="bb-check-mark" aria-hidden="true">
        {done ? "✓" : ""}
      </span>
      <span>
        {children}
        <span className="visually-hidden">{done ? " (gelukt)" : " (nog niet)"}</span>
      </span>
    </li>
  );
}

function isMac(): boolean {
  return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

function readArrangement(): Arrangement {
  try {
    return localStorage.getItem(ARRANGEMENT_KEY) === "telefoon" ? "telefoon" : "naast-elkaar";
  } catch {
    return "naast-elkaar";
  }
}

function RemoteQr({ link }: { link: string | null }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!link) {
      setSrc(null);
      return;
    }
    QRCode.toDataURL(link, { width: 200, margin: 2, color: { dark: "#1a3a38", light: "#ffffff" } })
      .then((url: string) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
    };
  }, [link]);
  if (!src) return null;
  return <img className="bb-qr" src={src} width={200} height={200} alt="QR-code voor de bediening op je telefoon" />;
}

const STEP_TITLES = [
  "Stuur de link naar je cliënt",
  "Je cliënt maakt verbinding",
  "Kies hoe je bedient",
  "Test de verbinding",
];

export function BeeldbellenWizard({ onClose }: { onClose: () => void }) {
  const state = useBeeldbellen();
  const [step, setStep] = useState(0);
  const [arrangement, setArrangement] = useState<Arrangement>(readArrangement);
  const [testState, setTestState] = useState<"idle" | "running" | "done" | "failed">("idle");
  const testTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!ensureBeeldbellenSession()) onClose();
  }, [onClose]);

  useEffect(
    () => () => {
      if (testTimer.current) window.clearTimeout(testTimer.current);
    },
    []
  );

  const chooseArrangement = (next: Arrangement) => {
    setArrangement(next);
    try {
      localStorage.setItem(ARRANGEMENT_KEY, next);
    } catch {
      /* voorkeur onthouden is optioneel */
    }
  };

  const startTestRound = () => {
    // Met een gekoppelde telefoon bedien je alleen daar.
    if (getBeeldbellenState().remoteConnected) return;
    window.__EMDR_SET_PLAYING__?.(true);
    setTestState("running");
    if (testTimer.current) window.clearTimeout(testTimer.current);
    testTimer.current = window.setTimeout(() => {
      window.__EMDR_SET_PLAYING__?.(false);
      setTestState("done");
    }, TEST_ROUND_MS);
  };

  const report = state.report;
  const connected = state.count > 0;
  const clientReady = connected && !!report?.ready;
  const smooth = clientReady && report?.fps !== null && (report?.fps ?? 0) >= SMOOTH_FPS_MIN;
  const last = step === STEP_TITLES.length - 1;

  return (
    <div
      className="bb-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bb-wizard-title"
      onClick={onClose}
    >
      <div className="bb-modal" onClick={(e) => e.stopPropagation()}>
        <header className="bb-modal-header">
          <div>
            <p className="bb-step-count">
              Beeldbellen · stap {step + 1} van {STEP_TITLES.length}
            </p>
            <h2 id="bb-wizard-title">{STEP_TITLES[step]}</h2>
          </div>
          <button type="button" className="jari-btn" onClick={onClose}>
            Sluiten
          </button>
        </header>

        <div className="bb-modal-body">
          {step === 0 && (
            <>
              <p>
                Je cliënt ziet de bal straks op het eigen scherm. Daardoor hapert hij niet, ook
                als de internetverbinding traag is. Jij bedient alles vanaf hier.
              </p>
              <ol className="bb-steps">
                <li>
                  Kopieer de uitnodiging met de knop hieronder.
                </li>
                <li>
                  Plak hem in de chat van je beeldbelgesprek (bijvoorbeeld Teams) met{" "}
                  <kbd>{isMac() ? "⌘" : "Ctrl"}</kbd> + <kbd>V</kbd> en verstuur het bericht.
                </li>
                <li>Vraag je cliënt de link te openen.</li>
              </ol>
              <CopyInvitationButton link={state.clientLink} />
              {state.clientLink && <p className="bb-link">{state.clientLink}</p>}
            </>
          )}

          {step === 1 && (
            <>
              <p>
                Je cliënt krijgt een nieuw tabblad en bevestigt daar met{" "}
                <strong>Ik ben er klaar voor</strong>. Daarna vult de pagina het hele scherm. Je
                cliënt ziet jou dan niet, maar hoort je wel.
              </p>
              <ul className="bb-checklist">
                <Check done={connected}>Je cliënt heeft de link geopend</Check>
                <Check done={clientReady}>Je cliënt heeft aangegeven er klaar voor te zijn</Check>
                <Check done={clientReady && !!report?.fullscreen}>De pagina staat op volledig scherm</Check>
                <Check done={!!smooth}>Het beeld loopt vloeiend</Check>
              </ul>
              {!clientReady && (
                <p className="bb-hint">De lijst wordt automatisch bijgewerkt.</p>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="bb-choice" role="radiogroup" aria-label="Bediening">
                <button
                  type="button"
                  role="radio"
                  aria-checked={arrangement === "naast-elkaar"}
                  className={`bb-choice-option${arrangement === "naast-elkaar" ? " is-selected" : ""}`}
                  onClick={() => chooseArrangement("naast-elkaar")}
                >
                  <span className="bb-choice-art bb-choice-art--split" aria-hidden="true">
                    <span>Beeldbellen</span>
                    <span>EMDR</span>
                  </span>
                  <strong>Bedien via laptop</strong>
                  <span>Beeldbellen en bediening op hetzelfde scherm.</span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={arrangement === "telefoon"}
                  className={`bb-choice-option${arrangement === "telefoon" ? " is-selected" : ""}`}
                  onClick={() => chooseArrangement("telefoon")}
                >
                  <span className="bb-choice-art bb-choice-art--phone" aria-hidden="true">
                    <span>Beeldbellen</span>
                    <span />
                  </span>
                  <strong>Bedien via telefoon</strong>
                  <span>Je cliënt groot in beeld, je telefoon als afstandsbediening.</span>
                </button>
              </div>

              {arrangement === "naast-elkaar" ? (
                <p>
                  Zet het beeldbellen en dit venster naast elkaar. Start en pauzeer met de knop
                  boven de bal, of met de spatiebalk.
                </p>
              ) : (
                <div className="bb-phone">
                  <RemoteQr link={state.remoteLink} />
                  <ol className="bb-steps">
                    <li>Open de camera van je telefoon en richt hem op de QR-code.</li>
                    <li>Open de link die verschijnt. Je bedient de tool nu met je telefoon.</li>
                    <li>
                      Laat dit tabblad open en ga terug naar je beeldbelgesprek. Dat kan nu
                      schermvullend.
                    </li>
                  </ol>
                  <p className={`bb-phone-status${state.remoteConnected ? " is-ok" : ""}`} role="status">
                    {state.remoteConnected ? "✓ Je telefoon is gekoppeld" : "Wachten op je telefoon…"}
                  </p>
                </div>
              )}

              <p className="bb-hint">
                Tijdens beeldbellen klinkt het geluid alleen bij je cliënt, niet op dit scherm.
              </p>
            </>
          )}

          {step === 3 && (
            <>
              <p>
                Laat de bal tien seconden lopen en vraag je cliënt of hij vloeiend beweegt.
              </p>
              <ClientStatusBanner presence={state} clientLink={state.clientLink} compact />
              <div className="bb-test">
                <button
                  type="button"
                  className="jari-btn jari-btn--primary"
                  onClick={startTestRound}
                  disabled={!clientReady || testState === "running" || state.remoteConnected}
                >
                  {testState === "running"
                    ? "De bal loopt…"
                    : testState === "idle"
                      ? "Start test"
                      : "Test opnieuw"}
                </button>
                {state.remoteConnected ? (
                  <span className="bb-hint">
                    Je bedient met je telefoon. Start en pauzeer de test daar.
                  </span>
                ) : (
                  !clientReady && (
                    <span className="bb-hint">Dit kan zodra je cliënt klaar is (stap 2).</span>
                  )
                )}
              </div>
              {(testState === "done" || testState === "failed" || state.remoteConnected) && (
                <div className="bb-test-result">
                  <p>
                    <strong>Zag je cliënt de bal vloeiend bewegen?</strong>
                  </p>
                  <div className="bb-status-actions">
                    <button type="button" className="jari-btn jari-btn--primary" onClick={onClose}>
                      Ja, beginnen
                    </button>
                    <button
                      type="button"
                      className="jari-btn jari-btn--secondary"
                      onClick={() => setTestState("failed")}
                    >
                      Nee
                    </button>
                  </div>
                  {testState === "failed" && (
                    <ul className="bb-steps">
                      <li>
                        Ziet je cliënt niets? Vraag of het tabblad «EMDR» vooraan staat en of je cliënt
                        heeft aangegeven er klaar voor te zijn.
                      </li>
                      <li>
                        Hapert de bal? Vraag je cliënt andere programma's en tabbladen te sluiten.
                      </li>
                      <li>Werkt het nog niet? Laat je cliënt de pagina verversen en test opnieuw.</li>
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <footer className="bb-modal-footer">
          <button
            type="button"
            className="jari-btn jari-btn--secondary"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
          >
            Vorige
          </button>
          {!last && (
            <button
              type="button"
              className="jari-btn jari-btn--primary"
              onClick={() => setStep((s) => Math.min(STEP_TITLES.length - 1, s + 1))}
            >
              Volgende
            </button>
          )}
          {last && (
            <button type="button" className="jari-btn jari-btn--secondary" onClick={onClose}>
              Klaar
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

/** Vaste balk op het grote scherm zolang er wordt gebeeldbeld. */
export function BeeldbellenBar({ onOpenWizard }: { onOpenWizard: () => void }) {
  const state = useBeeldbellen();

  const onPause = useCallback(() => {
    window.__EMDR_SET_PLAYING__?.(false);
  }, []);

  const onStop = () => {
    const ok = window.confirm(
      "Beeldbellen afsluiten? De bal stopt, je cliënt ziet dat de sessie is afgelopen en een gekoppelde telefoon wordt losgekoppeld."
    );
    if (ok) stopBeeldbellenSession();
  };

  if (!state.active || !state.started) return null;

  return (
    <div className="bb-bar">
      {state.hostStatus === "reconnecting" && (
        <div className="bb-status bb-status--lost" role="alert">
          <div className="bb-status-head">
            <span className="bb-status-icon" aria-hidden="true">
              !
            </span>
            <div className="bb-status-text">
              <strong>Je eigen verbinding is verbroken</strong>
              <span>
                De bal loopt bij je cliënt door, maar je kunt nu niet bedienen. Controleer je
                internetverbinding; de koppeling herstelt automatisch.
              </span>
            </div>
          </div>
        </div>
      )}
      <ClientStatusBanner
        presence={state}
        clientLink={state.clientLink}
        onPause={state.remoteConnected ? undefined : onPause}
        extraActions={
          <span className="bb-bar-actions">
            <button type="button" className="jari-btn" onClick={onOpenWizard}>
              Hulp bij opzet
            </button>
            <button type="button" className="jari-btn" onClick={onStop}>
              Afsluiten
            </button>
          </span>
        }
      />
    </div>
  );
}
